import React, { useMemo, useRef, useState, useCallback } from 'react';
import { Box, Stack, Paper, Divider, Typography, Slider } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import {
    useFieldArray,
    type Control,
    type FieldErrors,
    type UseFormSetValue,
} from 'react-hook-form';

import {
    SimpleButton,
    SimpleSwitch,
    ControlledTextField,
    ControlledSingleSelect,
    ControlledChipMultiSelect,
    ErrorMessage,
} from '@/src/ui';

import type {
    DropZone,
    RectDropZoneGeometry,
    CircleDropZoneGeometry,
    NormalizedPoint,
    QtiDragAndDropQuestionDTO,
} from '@/src/lib';
import {
    getImageSize,
    putImageBlob,
    addImageRef,
    releaseImageRef,
    getResolvedSrc,
} from '@/src/utils';
import { resizeToMax, extFromFileNameOrType } from '../utils/imageHelpers';

type Tool = 'select' | 'rect' | 'circle';

const HANDLE_SIZE = 8; // px

interface DDZoneEditorProps {
    control: Control<QtiDragAndDropQuestionDTO>;
    errors: FieldErrors<QtiDragAndDropQuestionDTO>;
    question: QtiDragAndDropQuestionDTO;
    currentLanguage: 'en' | 'es';
    setValue: UseFormSetValue<QtiDragAndDropQuestionDTO>;
}

interface DragState {
    kind: 'move' | 'resize' | null;
    zoneIndex?: number;
    handle?: 'se' | 'r';
    startPx?: { x: number; y: number };
    startZone?: DropZone;
}

export default function DDZoneEditor(props: DDZoneEditorProps) {
    const { control, errors, currentLanguage, question, setValue } = props;

    const {
        backgroundImageUrl,
        backgroundImageUrl_es,
        designCanvas,
        designCanvas_es,
        choices,
        zones,
    } = question;
    const activeCanvas = currentLanguage === 'en' ? designCanvas : designCanvas_es;

    // Zones array from RHF (source of truth)
    const { append, remove, update, insert } = useFieldArray({
        control,
        name: 'zones',
    });

    const [tool, setTool] = useState<Tool>('select');
    const [zoom, setZoom] = useState<number>(1);
    const [showGrid, setShowGrid] = useState<boolean>(false);
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

    const containerRef = useRef<HTMLDivElement>(null);
    const svgRef = useRef<SVGSVGElement>(null);
    const [drag, setDrag] = useState<DragState>({ kind: null });

    // Translate between px and normalized
    const toNorm = useCallback(
        (px: { x: number; y: number }): NormalizedPoint => ({
            x: snap(px.x / (activeCanvas.width * zoom)),
            y: snap(px.y / (activeCanvas.height * zoom)),
        }),
        [activeCanvas, zoom]
    );

    const toPx = useCallback(
        (np: NormalizedPoint): { x: number; y: number } => ({
            x: np.x * activeCanvas.width * zoom,
            y: np.y * activeCanvas.height * zoom,
        }),
        [activeCanvas, zoom]
    );

    const getMouse = (e: React.MouseEvent) => {
        const svg = svgRef.current!;
        const rect = svg.getBoundingClientRect();
        return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const nextZIndex = () =>
        (zones.length ? (zones[zones.length - 1].zIndex ?? zones.length - 1) : 0) + 1;

    const startRect = (start: { x: number; y: number }) => {
        const id = `rect-${uuidv4()}`;
        const norm = toNorm(start);
        const zone: DropZone = {
            id,
            label: `Rect ${id.slice(-4)}`,
            zIndex: nextZIndex(),
            accepts: [],
            maxItems: 1,
            snap: 'center',
            geometry: {
                type: 'rect',
                x: norm.x,
                y: norm.y,
                width: 0.05,
                height: 0.05,
                cornerRadius: 0,
            },
            geometry_es: {
                type: 'rect',
                x: norm.x,
                y: norm.y,
                width: 0.05,
                height: 0.05,
                cornerRadius: 0,
            },
        };
        append(zone as any);
        setSelectedIndex(zones.length); // newly appended index
        setDrag({
            kind: 'resize',
            zoneIndex: zones.length,
            handle: 'se',
            startPx: start,
            startZone: zone,
        });
    };

    const startCircle = (start: { x: number; y: number }) => {
        const id = `circle-${uuidv4()}`;
        const c = toNorm(start);
        const zone: DropZone = {
            id,
            label: `Circle ${id.slice(-4)}`,
            zIndex: nextZIndex(),
            accepts: [],
            maxItems: 1,
            snap: 'center',
            geometry: {
                type: 'circle',
                cx: c.x,
                cy: c.y,
                r: 0.05,
            },
            geometry_es: {
                type: 'circle',
                cx: c.x,
                cy: c.y,
                r: 0.05,
            },
        };
        append(zone as any);
        setSelectedIndex(zones.length);
        setDrag({
            kind: 'resize',
            zoneIndex: zones.length,
            handle: 'r',
            startPx: start,
            startZone: zone,
        });
    };

    const onSvgMouseDown = (e: React.MouseEvent) => {
        const pos = getMouse(e);
        if (tool === 'rect') return startRect(pos);
        if (tool === 'circle') return startCircle(pos);
        if (tool === 'select') setSelectedIndex(null);
    };

    const onSvgMouseMove = (e: React.MouseEvent) => {
        const pos = getMouse(e);
        if (!drag.kind || drag.zoneIndex == null) return;

        const dz = zones[drag.zoneIndex];
        if (!dz || !drag.startZone || !drag.startPx) return;

        // Use geometry or geometry_es based on currentLanguage
        const getGeometry = (zone: any) =>
            currentLanguage === 'en' ? zone.geometry : zone.geometry_es;
        const setGeometry = (zone: any, geometry: any) =>
            currentLanguage === 'en' ? { ...zone, geometry } : { ...zone, geometry_es: geometry };

        if (drag.kind === 'resize') {
            const dzGeom = getGeometry(dz);
            const startGeom = getGeometry(drag.startZone);
            if (dzGeom.type === 'rect') {
                const startNorm = startGeom as RectDropZoneGeometry;
                const current = toNorm(pos);
                const x0 = startNorm.x;
                const y0 = startNorm.y;
                let w = current.x - x0;
                let h = current.y - y0;
                let nx = x0;
                let ny = y0;
                if (w < 0) {
                    nx = x0 + w;
                    w = Math.abs(w);
                }
                if (h < 0) {
                    ny = y0 + h;
                    h = Math.abs(h);
                }

                nx = snap(nx);
                ny = snap(ny);

                const nextW = Math.max(snap(w), 0.002);
                const nextH = Math.max(snap(h), 0.002);

                const newGeom = {
                    ...startNorm,
                    type: 'rect',
                    x: nx,
                    y: ny,
                    width: nextW,
                    height: nextH,
                    cornerRadius: startNorm.cornerRadius,
                };
                const next = setGeometry(dz, newGeom);
                update(drag.zoneIndex, next as any);
            } else if (dzGeom.type === 'circle') {
                const start = startGeom as CircleDropZoneGeometry;
                const centerPx = toPx({ x: start.cx, y: start.cy });
                const dx = pos.x - centerPx.x;
                const dy = pos.y - centerPx.y;
                const rp = Math.sqrt(dx * dx + dy * dy);
                const rn = rp / (activeCanvas.width * zoom);
                const nextR = Math.max(snap(rn), 0.002);
                const newGeom = { ...start, type: 'circle', r: nextR };
                const next = setGeometry(dz, newGeom);
                update(drag.zoneIndex, next as any);
            }
        }

        if (drag.kind === 'move') {
            const dzGeom = getGeometry(dz);
            const startGeom = getGeometry(drag.startZone);
            const deltaPx = { x: pos.x - drag.startPx.x, y: pos.y - drag.startPx.y };
            const deltaNorm = {
                x: deltaPx.x / (activeCanvas.width * zoom),
                y: deltaPx.y / (activeCanvas.height * zoom),
            };
            if (dzGeom.type === 'rect') {
                const s = startGeom as RectDropZoneGeometry;
                const newGeom = {
                    ...dzGeom,
                    x: snap(s.x + deltaNorm.x),
                    y: snap(s.y + deltaNorm.y),
                };
                const next = setGeometry(dz, newGeom);
                update(drag.zoneIndex, next as any);
            } else {
                const s = startGeom as CircleDropZoneGeometry;
                const newGeom = {
                    ...dzGeom,
                    cx: snap(s.cx + deltaNorm.x),
                    cy: snap(s.cy + deltaNorm.y),
                };
                const next = setGeometry(dz, newGeom);
                update(drag.zoneIndex, next as any);
            }
        }
    };

    const onSvgMouseUp = () => setDrag({ kind: null });

    const beginMove = (index: number) => (e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedIndex(index);
        setDrag({
            kind: 'move',
            zoneIndex: index,
            startPx: getMouse(e),
            startZone: deepCopy(zones[index]),
        });
    };

    const beginResizeRect = (index: number, _handle: 'se') => (e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedIndex(index);
        setDrag({
            kind: 'resize',
            zoneIndex: index,
            handle: 'se',
            startPx: getMouse(e),
            startZone: deepCopy(zones[index]),
        });
    };

    const beginResizeCircle = (index: number) => (e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedIndex(index);
        setDrag({
            kind: 'resize',
            zoneIndex: index,
            handle: 'r',
            startPx: getMouse(e),
            startZone: deepCopy(zones[index]),
        });
    };

    // UI grid
    const gridLines = useMemo(() => {
        if (!showGrid) return null;
        const step = 0.1;
        const lines: React.JSX.Element[] = [];
        for (let t = step; t < 1; t += step) {
            const vx = toPx({ x: t, y: 0 }).x;
            const vy = toPx({ x: 0, y: t }).y;
            lines.push(
                <line
                    key={`v-${t}`}
                    x1={vx}
                    y1={0}
                    x2={vx}
                    y2={activeCanvas.height * zoom}
                    stroke="#eee"
                    strokeWidth={1}
                />,
                <line
                    key={`h-${t}`}
                    x1={0}
                    y1={vy}
                    x2={activeCanvas.width * zoom}
                    y2={vy}
                    stroke="#eee"
                    strokeWidth={1}
                />
            );
        }
        return lines;
    }, [showGrid, activeCanvas, zoom, toPx]);

    const selectedZone = selectedIndex != null ? zones[selectedIndex] : null;

    const removeSelected = () => {
        if (selectedIndex == null) return;
        remove(selectedIndex);
        setSelectedIndex(null);
    };

    const duplicateSelected = () => {
        if (selectedIndex == null || !selectedZone) return;
        const clone: DropZone = {
            ...deepCopy(selectedZone),
            id: `zone-${uuidv4()}`,
            label: `${selectedZone.label} (copy)`,
            zIndex: nextZIndex(),
        };
        insert(selectedIndex + 1, clone as any);
        setSelectedIndex(selectedIndex + 1);
    };

    // Handle image upload
    const onPickFile = async (file: File) => {
        if (!file) return;

        if (
            currentLanguage === 'en' &&
            backgroundImageUrl &&
            backgroundImageUrl.startsWith('local://')
        ) {
            const guid = backgroundImageUrl.slice('local://'.length);
            releaseImageRef(guid);
        } else if (
            currentLanguage === 'es' &&
            backgroundImageUrl_es &&
            backgroundImageUrl_es.startsWith('local://')
        ) {
            const guid = backgroundImageUrl_es.slice('local://'.length);
            releaseImageRef(guid);
        }

        const resized = await resizeToMax(file, file.type || 'image/png');

        const fileName = `${uuidv4()}${extFromFileNameOrType(file.name, file.type)}`;

        putImageBlob(fileName, resized);

        const localUrl = `local://${fileName}`;
        const { width: w, height: h } = await getImageSize(resized);

        if (currentLanguage === 'en') {
            setValue('backgroundImageUrl', localUrl);
            setValue('designCanvas', { width: w, height: h });
        } else {
            setValue('backgroundImageUrl_es', localUrl);
            setValue('designCanvas_es', { width: w, height: h });
        }

        addImageRef(fileName);
    };

    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const fileInputRef_es = useRef<HTMLInputElement | null>(null);

    return (
        <Stack direction="column" spacing={2}>
            <Paper elevation={1} sx={{ p: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <SimpleButton
                        onClick={() => setTool('select')}
                        variant={tool === 'select' ? 'contained' : 'outlined'}
                    >
                        Select
                    </SimpleButton>
                    <SimpleButton
                        onClick={() => setTool('rect')}
                        variant={tool === 'rect' ? 'contained' : 'outlined'}
                    >
                        Rect
                    </SimpleButton>
                    <SimpleButton
                        onClick={() => setTool('circle')}
                        variant={tool === 'circle' ? 'contained' : 'outlined'}
                    >
                        Circle
                    </SimpleButton>

                    <Divider flexItem orientation="vertical" sx={{ mx: 1 }} />

                    <Box sx={{ width: 180, px: 2 }}>
                        <Typography variant="caption">Zoom</Typography>
                        <Slider
                            size="small"
                            min={0.5}
                            max={2}
                            step={0.1}
                            value={zoom}
                            onChange={(_, v) => setZoom(v as number)}
                        />
                    </Box>
                    <SimpleSwitch
                        name="showGrid"
                        label="Show Grid"
                        checked={showGrid}
                        onChange={(e) => setShowGrid(e.target.checked)}
                    />
                    {currentLanguage === 'en' ? (
                        <>
                            <SimpleButton onClick={() => fileInputRef.current?.click()}>
                                Upload Background Image
                            </SimpleButton>
                            <input
                                type="file"
                                accept="image/*"
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) onPickFile(file);
                                }}
                            />
                            <ErrorMessage error={errors.backgroundImageUrl} />
                        </>
                    ) : (
                        <>
                            <SimpleButton onClick={() => fileInputRef_es.current?.click()}>
                                Upload Background Image (Spanish)
                            </SimpleButton>
                            <input
                                type="file"
                                accept="image/*"
                                ref={fileInputRef_es}
                                style={{ display: 'none' }}
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) onPickFile(file);
                                }}
                            />
                            <ErrorMessage error={errors.backgroundImageUrl_es} />
                        </>
                    )}
                </Stack>

                <div className="my-4" />

                {(backgroundImageUrl && currentLanguage === 'en') ||
                (backgroundImageUrl_es && currentLanguage === 'es') ? (
                    <Box
                        ref={containerRef}
                        sx={{
                            mt: 1,
                            position: 'relative',
                            width: activeCanvas.width * zoom,
                            height: activeCanvas.height * zoom,
                            backgroundImage:
                                currentLanguage === 'en'
                                    ? `url(${getResolvedSrc(backgroundImageUrl)})`
                                    : `url(${getResolvedSrc(backgroundImageUrl_es)})`,
                            backgroundRepeat: 'no-repeat',
                            backgroundSize: '100% 100%',
                            border: '1px solid #ddd',
                            borderRadius: 1,
                            overflow: 'hidden',
                            cursor: tool === 'select' ? 'default' : 'crosshair',
                        }}
                    >
                        <svg
                            ref={svgRef}
                            width={activeCanvas.width * zoom}
                            height={activeCanvas.height * zoom}
                            onMouseDown={onSvgMouseDown}
                            onMouseMove={onSvgMouseMove}
                            onMouseUp={onSvgMouseUp}
                            style={{ display: 'block' }}
                        >
                            {gridLines}

                            {zones
                                .map((z, i) => ({ z, i }))
                                .sort((a, b) => (a.z.zIndex ?? 0) - (b.z.zIndex ?? 0))
                                .map(({ z, i }) => {
                                    const isSelected = i === selectedIndex;
                                    const isError = errors.zones?.[i];
                                    const g = currentLanguage === 'en' ? z.geometry : z.geometry_es;

                                    let fill = '#1976d25f';
                                    if (isError) fill = '#F399995f';

                                    let stroke = '#00000066';
                                    if (isError) stroke = '#F39999';
                                    else if (isSelected) stroke = '#1976d2';
                                    if (g.type === 'rect') {
                                        const p = toPx({ x: g.x, y: g.y });
                                        const w = g.width * activeCanvas.width * zoom;
                                        const h = g.height * activeCanvas.height * zoom;
                                        if (w < 0 || h < 0) return null;
                                        return (
                                            <g key={z.id} onMouseDown={beginMove(i)}>
                                                <rect
                                                    x={p.x}
                                                    y={p.y}
                                                    width={w}
                                                    height={h}
                                                    rx={Math.max(
                                                        0,
                                                        (g.cornerRadius ?? 0) * Math.min(w, h)
                                                    )}
                                                    ry={Math.max(
                                                        0,
                                                        (g.cornerRadius ?? 0) * Math.min(w, h)
                                                    )}
                                                    fill={fill}
                                                    stroke={stroke}
                                                    strokeWidth={isSelected ? 2 : 1}
                                                />
                                                {isSelected &&
                                                    renderHandlesForRect(
                                                        i,
                                                        g,
                                                        toPx,
                                                        beginResizeRect
                                                    )}
                                            </g>
                                        );
                                    } else {
                                        const c = toPx({ x: g.cx, y: g.cy });
                                        const r = g.r * activeCanvas.width * zoom;
                                        if (r < 0) return null;
                                        return (
                                            <g key={z.id} onMouseDown={beginMove(i)}>
                                                <circle
                                                    cx={c.x}
                                                    cy={c.y}
                                                    r={r}
                                                    fill={fill}
                                                    stroke={stroke}
                                                    strokeWidth={isSelected ? 2 : 1}
                                                />
                                                {isSelected &&
                                                    renderHandlesForCircle(
                                                        i,
                                                        g,
                                                        toPx,
                                                        beginResizeCircle
                                                    )}
                                            </g>
                                        );
                                    }
                                })}
                        </svg>
                    </Box>
                ) : (
                    <div className="p-2">Upload an image to get started.</div>
                )}
            </Paper>

            <Stack direction="row" spacing={2}>
                <Paper elevation={1} sx={{ width: 300, p: 2, mr: 2 }}>
                    <Typography variant="h6">Zones</Typography>
                    <Stack spacing={1} sx={{ overflowY: 'auto', maxHeight: '50vh' }}>
                        {!zones.length && (
                            <Typography variant="body2" color="text.secondary">
                                No zones set
                            </Typography>
                        )}
                        {zones.map((zone, i) => {
                            const isError = errors.zones?.[i];
                            const isSelected = i === selectedIndex;

                            let borderColor = '1px solid #CCCCCC';
                            if (isError) {
                                if (isSelected) {
                                    borderColor = '2px solid #FF8888';
                                } else {
                                    borderColor = '1px solid #FFBBBB';
                                }
                            } else if (isSelected) {
                                borderColor = '2px solid #1976d2';
                            }

                            let backgroundColor = undefined;
                            if (isError) {
                                backgroundColor = '#FFBBBB14';
                            } else if (isSelected) {
                                backgroundColor = '#1976d214';
                            }

                            const geometry =
                                currentLanguage === 'en' ? zone.geometry : zone.geometry_es;
                            return (
                                <Box
                                    key={zone.id}
                                    sx={{
                                        p: 1,
                                        border: borderColor,
                                        borderRadius: 1,
                                        cursor: 'pointer',
                                        background: backgroundColor,
                                        transition: 'background 0.2s, border 0.2s',
                                        '&:hover': {
                                            background: 'rgba(25,118,210,0.04)',
                                        },
                                    }}
                                    onClick={() => setSelectedIndex(i)}
                                >
                                    {geometry.type === 'rect' ? (
                                        <>
                                            <Typography variant="subtitle2">
                                                Rectangle - {zone.label}
                                            </Typography>
                                            <Typography variant="caption">
                                                (x, y): {geometry.x}, {geometry.y}
                                            </Typography>
                                            <br />
                                            <Typography variant="caption">
                                                width: {geometry.width}, height: {geometry.height}
                                            </Typography>
                                            <br />
                                            <Typography variant="caption">
                                                max items: {zone.maxItems}, accepts:{' '}
                                                {zone.accepts.length
                                                    ? zone.accepts.join(', ')
                                                    : 'All'}
                                                , snap: {zone.snap}
                                            </Typography>
                                        </>
                                    ) : geometry.type === 'circle' ? (
                                        <>
                                            <Typography variant="subtitle2">
                                                Circle - {zone.label}
                                            </Typography>
                                            <Typography variant="caption">
                                                (cx, cy): {geometry.cx}, {geometry.cy}
                                            </Typography>
                                            <br />
                                            <Typography variant="caption">
                                                radius: {geometry.r}
                                            </Typography>
                                            <br />
                                            <Typography variant="caption">
                                                max items: {zone.maxItems}, accepts:{' '}
                                                {zone.accepts.length
                                                    ? zone.accepts.join(', ')
                                                    : 'All'}
                                                , snap: {zone.snap}
                                            </Typography>
                                        </>
                                    ) : (
                                        <Typography variant="subtitle2">
                                            Unknown shape type
                                        </Typography>
                                    )}
                                    <ErrorMessage
                                        error={isError ? 'Errors need to be corrected' : ''}
                                    />
                                </Box>
                            );
                        })}
                    </Stack>
                </Paper>

                <Paper elevation={1} sx={{ width: 700, p: 2, mr: 2 }}>
                    <Typography variant="h6">Zone Properties</Typography>
                    {selectedIndex != null && selectedZone ? (
                        <Stack
                            direction="row"
                            spacing={2}
                            alignItems="flex-start"
                            key={`${selectedIndex}-${JSON.stringify(selectedZone?.[currentLanguage === 'en' ? 'geometry' : 'geometry_es'])}`}
                        >
                            <Stack spacing={2} flex={1}>
                                <Stack direction="row" spacing={1}>
                                    <SimpleButton color="error" onClick={removeSelected}>
                                        Delete
                                    </SimpleButton>
                                    <SimpleButton onClick={duplicateSelected}>Copy</SimpleButton>
                                </Stack>
                                <ControlledTextField
                                    name={`zones.${selectedIndex}.label`}
                                    control={control}
                                    label="Label"
                                    placeholder="Zone label"
                                    error={errors.zones?.[selectedIndex]?.label}
                                />
                                <ControlledTextField
                                    name={`zones.${selectedIndex}.maxItems`}
                                    control={control}
                                    label="Max Items"
                                    type="number"
                                    placeholder="Enter max items"
                                    error={errors.zones?.[selectedIndex]?.maxItems}
                                />
                                <ControlledSingleSelect
                                    name={`zones.${selectedIndex}.snap`}
                                    control={control}
                                    options={['none', 'center', 'origin'].map((o) => ({
                                        id: o,
                                        name: o,
                                    }))}
                                    error={errors.zones?.[selectedIndex]?.snap}
                                    label="Snap"
                                />
                                <ControlledChipMultiSelect
                                    name={`zones.${selectedIndex}.accepts`}
                                    control={control}
                                    options={choices.map((c) => ({ id: c.id, name: c.label }))}
                                    error={errors.zones?.[selectedIndex]?.accepts}
                                    label="Accepted choices (blank for all)"
                                />
                            </Stack>
                            <Divider orientation="vertical" flexItem />
                            <Stack spacing={1} flex={1}>
                                <Typography variant="subtitle2">Geometry</Typography>
                                {selectedZone[
                                    `${currentLanguage === 'en' ? 'geometry' : 'geometry_es'}`
                                ].type === 'rect' && (
                                    <Stack spacing={1}>
                                        {(
                                            ['x', 'y', 'width', 'height', 'cornerRadius'] as const
                                        ).map((key) => (
                                            <ControlledTextField
                                                key={key}
                                                name={`zones.${selectedIndex}.${currentLanguage === 'en' ? 'geometry' : 'geometry_es'}.${key}`}
                                                control={control}
                                                label={key}
                                                type="number"
                                                inputProps={{ step: 0.005, min: 0, max: 1 }}
                                                onChange={(e, field) => {
                                                    field.onChange(snap(e.target.value));
                                                }}
                                                placeholder={`Enter ${key}`}
                                            />
                                        ))}
                                    </Stack>
                                )}
                                {selectedZone[
                                    `${currentLanguage === 'en' ? 'geometry' : 'geometry_es'}`
                                ].type === 'circle' && (
                                    <Stack spacing={1}>
                                        {(['cx', 'cy', 'r'] as const).map((key) => (
                                            <ControlledTextField
                                                key={key}
                                                name={`zones.${selectedIndex}.${currentLanguage === 'en' ? 'geometry' : 'geometry_es'}.${key}`}
                                                control={control}
                                                label={key}
                                                type="number"
                                                inputProps={{ step: 0.005, min: 0, max: 1 }}
                                                onChange={(e, field) => {
                                                    field.onChange(snap(e.target.value));
                                                }}
                                                placeholder={`Enter ${key}`}
                                            />
                                        ))}
                                    </Stack>
                                )}
                            </Stack>
                        </Stack>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            Select a zone to edit its properties.
                        </Typography>
                    )}
                </Paper>
            </Stack>
        </Stack>
    );
}

// Drawing Helpers
function renderHandlesForRect(
    index: number,
    g: RectDropZoneGeometry,
    toPx: (p: NormalizedPoint) => { x: number; y: number },
    beginResizeRect: (i: number, handle: 'se') => (e: React.MouseEvent) => void
) {
    const p = toPx({ x: g.x, y: g.y });
    const w = g.width; // normalized
    const h = g.height; // normalized
    const wpx = w * (toPx({ x: 1, y: 0 }).x - toPx({ x: 0, y: 0 }).x);
    const hpx = h * (toPx({ x: 0, y: 1 }).y - toPx({ x: 0, y: 0 }).y);
    const corners = [{ id: 'se', x: p.x + wpx, y: p.y + hpx }];

    return corners.map((c) => (
        <rect
            key={c.id}
            x={c.x - HANDLE_SIZE / 2}
            y={c.y - HANDLE_SIZE / 2}
            width={HANDLE_SIZE}
            height={HANDLE_SIZE}
            fill="#fff"
            stroke="#1976d2"
            strokeWidth={1}
            onMouseDown={beginResizeRect(index, 'se')}
        />
    ));
}

function renderHandlesForCircle(
    index: number,
    g: CircleDropZoneGeometry,
    toPx: (p: NormalizedPoint) => { x: number; y: number },
    beginResizeCircle: (i: number) => (e: React.MouseEvent) => void
) {
    const c = toPx({ x: g.cx, y: g.cy });
    const rPx = g.r * (toPx({ x: 1, y: 0 }).x - toPx({ x: 0, y: 0 }).x);
    const handle = { x: c.x + rPx, y: c.y };
    return (
        <circle
            cx={handle.x}
            cy={handle.y}
            r={HANDLE_SIZE / 2}
            fill="#fff"
            stroke="#1976d2"
            strokeWidth={1}
            onMouseDown={beginResizeCircle(index)}
        />
    );
}

// Utils
const SNAP_STEP = 0.005;
function snap(n: number | string, step = SNAP_STEP) {
    const k = Math.round(1 / step); // 200 for 0.005
    return clamp(Math.round(Number(n) * k) / k, 0, 1);
}
function clamp(n: number, min: number, max: number) {
    return Math.max(min, Math.min(max, n));
}
function deepCopy<T>(o: T): T {
    return JSON.parse(JSON.stringify(o));
}
